'use client';

import React, { useEffect, useMemo, useState, useContext } from 'react';
import { Progress } from 'antd';
import { FaPlus } from 'react-icons/fa';
import { useWatchlistContext } from '@benzinga/user-context';
import { SessionContext } from '@benzinga/session-context';
import { Watchlist } from '@benzinga/watchlist-manager';

import { ErrorBoundary } from '@benzinga/core-ui';

import {
  getWatchlistFinancialsCacheKey,
  getWatchlistEnrichment,
  getWatchlistQuotesWithProgress,
  invalidateWatchlistFinancialsCache,
  isWatchlistFinancialsDegraded,
  WatchlistFinancials,
} from '../../data';

import {
  OverviewColumnsDef,
  WatchlistColumnsDef,
  RankingsColumnsDef,
  CryptoOverviewColumnsDef,
  FundamentalsColumnsDef,
  AnalystRatingsColumnsDef,
} from './ColumnDefs';
import { WatchlistQuotesTable } from './WatchlistQuotesTable';
import { TabPaywall } from './TabPaywall';
import { ColumnDef, TableVirtualized } from '@benzinga/table';
import { isCryptoOnlyWatchlist } from '../watchlistUtils';
import { syncWatchlistSymbolsIntoRows } from '../watchlistAlertStatus';
import { WatchlistQuotesTableSkeleton, ResearchTableSkeleton } from '../WatchlistInsightsSkeleton';
import { TickerLogo } from '../../../../edge/dashboard/_components/TickerLogo';
import { useTickerLogos } from '../../../../edge/dashboard/_components/useTickerLogos';
import { DashboardPillTabs } from '../../../../edge/dashboard/_components/DashboardPillTabs';
import { WidgetStatus } from '../../../../edge/dashboard/_components/WidgetStatus';
import {
  widgetGhostButtonClassName,
  widgetPrimaryButtonClassName,
} from '../../../../edge/dashboard/_components/widgetChrome';
import { PortfolioSectionHeader } from '../PortfolioSectionHeader';
import { WATCHLIST_QUOTES_TABLE_FRAME_CLASS, WATCHLIST_QUOTES_SYMBOL_CELL_CLASS } from '../portfolioLayoutConstants';
import { ResearchTableLoadingProvider, TickerLogoLookupProvider } from './utils';

const CRYPTO_ONLY_TAB_KEYS = new Set(['overview']);
const currencyFormatter = Intl.NumberFormat('en-US', { currency: 'USD', maximumFractionDigits: 2, style: 'currency' });
const compactFormatter = Intl.NumberFormat('en-US', { maximumFractionDigits: 1, notation: 'compact' });

const getMobileRowLabel = (row: WatchlistFinancials): string | null => {
  const rawName =
    row.quote?.type === 'CRYPTO'
      ? row.quote?.description
      : (row.quote?.companyStandardName ?? row.financials?.company?.standardName);

  if (rawName) {
    const byIndex = rawName.lastIndexOf(' by ');
    return byIndex > 0 ? rawName.slice(byIndex + 4).trim() : rawName;
  }

  const volume = row.quote?.volume;
  if (row.quote?.lastTradePrice && volume != null) {
    return `Vol ${compactFormatter.format(volume)}`;
  }

  return null;
};

interface FinancialTableProps {
  tableHeight?: number;
  variant?: 'research' | 'watchlist';
}

export const FinancialTable = ({ tableHeight = 500, variant = 'research' }: FinancialTableProps) => {
  const { selectedWatchlist, setAddTickerModal } = useWatchlistContext();
  const session = useContext(SessionContext);
  const isCryptoOnly = isCryptoOnlyWatchlist(selectedWatchlist);
  const [tabKey, setTabKey] = useState(variant === 'watchlist' ? 'watchlist' : 'analyst-ratings');
  const [rowData, setRowData] = useState<WatchlistFinancials[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isEnriching, setIsEnriching] = useState(false);
  const [hasFetchError, setHasFetchError] = useState(false);
  const [retryCount, setRetryCount] = useState(0);
  const [loadingProgress, setLoadingProgress] = useState<{
    loaded: number;
    total: number;
    percent: number;
  } | null>(null);

  const handleChangeTab = (newTab: string) => {
    if (isLoading || loadingProgress) return;
    setTabKey(newTab);
  };

  const tabsData = useMemo(() => {
    if (variant === 'watchlist') {
      return [
        {
          key: 'watchlist',
          name: 'Watchlist',
          type: 'table',
          columns: isCryptoOnly ? CryptoOverviewColumnsDef : WatchlistColumnsDef,
        },
      ];
    }

    const allTabs = [
      {
        key: 'analyst-ratings',
        name: 'Analyst Ratings',
        type: 'table',
        columns: AnalystRatingsColumnsDef,
      },
      {
        key: 'fundamentals',
        name: 'Fundamentals',
        type: 'table',
        columns: FundamentalsColumnsDef,
      },
      {
        key: 'rankings',
        isGated: true,
        type: 'table',
        name: 'Edge Rankings',
        columns: RankingsColumnsDef,
      },
      {
        key: 'overview',
        name: 'Metrics',
        type: 'table',
        columns: isCryptoOnly ? CryptoOverviewColumnsDef : OverviewColumnsDef,
      },
    ];

    if (!isCryptoOnly) return allTabs;
    return allTabs.filter(tab => CRYPTO_ONLY_TAB_KEYS.has(tab.key));
  }, [isCryptoOnly, variant]);

  const activeTabData = useMemo(() => {
    const key = tabsData.some(tab => tab.key === tabKey) ? tabKey : tabsData[0]?.key;
    return tabsData.find(tab => tab.key === key);
  }, [tabKey, tabsData]);

  const selectedWatchlistDataKey = selectedWatchlist ? getWatchlistFinancialsCacheKey(selectedWatchlist) : '';
  const selectedWatchlistRef = React.useRef(selectedWatchlist);
  selectedWatchlistRef.current = selectedWatchlist;

  const handleRetryFetch = () => {
    if (selectedWatchlist) {
      invalidateWatchlistFinancialsCache(getWatchlistFinancialsCacheKey(selectedWatchlist));
    }
    setRetryCount(count => count + 1);
  };

  useEffect(() => {
    const abortController = new AbortController();
    const watchlist = selectedWatchlistRef.current;

    const getTableData = async (nextWatchlist: Watchlist) => {
      setRowData([]);
      setIsLoading(true);
      setIsEnriching(false);
      setHasFetchError(false);
      setLoadingProgress(null);
      try {
        const quoteRows = await getWatchlistQuotesWithProgress(
          nextWatchlist,
          progress => {
            if (!abortController.signal.aborted) {
              setLoadingProgress(progress);
            }
          },
          session.getSession(),
        );
        if (abortController.signal.aborted) return;

        const syncedQuoteRows = syncWatchlistSymbolsIntoRows(
          quoteRows,
          selectedWatchlistRef.current?.symbols ?? [],
        );
        setRowData(syncedQuoteRows);

        if (syncedQuoteRows.length === 0 && nextWatchlist.symbols.length > 0) {
          setHasFetchError(true);
          return;
        }

        if (variant === 'research') {
          setIsEnriching(true);
          try {
            const enrichedRows = await getWatchlistEnrichment(
              nextWatchlist,
              syncedQuoteRows,
              session.getSession(),
            );
            if (abortController.signal.aborted) return;
            setRowData(syncWatchlistSymbolsIntoRows(enrichedRows, selectedWatchlistRef.current?.symbols ?? []));
          } catch (enrichmentError) {
            if (enrichmentError instanceof Error && enrichmentError.name === 'AbortError') return;
            console.error('Error fetching watchlist enrichment data', enrichmentError);
          } finally {
            if (!abortController.signal.aborted) {
              setIsEnriching(false);
            }
          }
        }
      } catch (error) {
        if (error instanceof Error && error.name === 'AbortError') return;
        console.error('Error fetching financial table data', error);
        setHasFetchError(true);
      } finally {
        if (!abortController.signal.aborted) {
          setIsLoading(false);
          setLoadingProgress(null);
        }
      }
    };

    if (watchlist?.symbols && watchlist.symbols.length > 0) {
      getTableData(watchlist);
    } else {
      setRowData([]);
      setIsLoading(false);
      setHasFetchError(false);
      setLoadingProgress(null);
    }

    return () => {
      abortController.abort();
    };
  }, [selectedWatchlistDataKey, session, retryCount, variant]);

  const tableRows = syncWatchlistSymbolsIntoRows(rowData, selectedWatchlist?.symbols ?? []);
  const tableSymbols = useMemo(() => tableRows.map(row => row.symbol), [tableRows]);
  const logoBySymbol = useTickerLogos(tableSymbols);
  const researchSkeletonColumns = useMemo(
    () => (activeTabData?.columns ?? []).map(column => column.headerName ?? column.field),
    [activeTabData?.columns],
  );
  const hasRankingsError =
    !hasFetchError &&
    !isLoading &&
    !isEnriching &&
    isWatchlistFinancialsDegraded(tableRows, selectedWatchlist?.symbols.length ?? 0);

  if (!selectedWatchlist?.symbols.length) {
    const emptyState = (
      <WidgetStatus
        action={
          <button
            type="button"
            className={widgetPrimaryButtonClassName}
            onClick={() => setAddTickerModal({ show: true, ticker: null, isLoading: false })}
          >
            <FaPlus size={12} />
            Add tickers
          </button>
        }
        className="m-3 min-h-64"
        description="Add companies or paste a ticker list to start tracking quotes, rankings, and research."
        title="This watchlist is ready for its first ticker"
      />
    );

    if (variant !== 'research') return emptyState;

    return (
      <div className="financial-table financial-table--research flex min-h-0 flex-1 flex-col">
        <PortfolioSectionHeader
          title="Research"
          subtitle="Ratings, fundamentals, and rankings for this watchlist"
        />
        {emptyState}
      </div>
    );
  }

  return (
    <ErrorBoundary name="FinancialTable">
      <TickerLogoLookupProvider logos={logoBySymbol}>
      <div
        className={`financial-table ${variant === 'research' ? `financial-table--research financial-table--${activeTabData?.key ?? 'analyst-ratings'} flex min-h-0 flex-1 flex-col` : ''}`}
      >
        {variant === 'research' && (
          <div className="shrink-0 border-b border-slate-200">
            <PortfolioSectionHeader
              bordered={false}
              title="Research"
              subtitle="Ratings, fundamentals, and rankings for this watchlist"
            />
            <div className="px-3 pb-2">
              <DashboardPillTabs
                activeTab={activeTabData?.key ?? tabsData[0]?.key ?? 'analyst-ratings'}
                density="compact"
                onChange={handleChangeTab}
                tabs={tabsData.map(tab => ({ key: tab.key, name: tab.name }))}
              />
            </div>
          </div>
        )}
        {loadingProgress && loadingProgress.total > 150 && (
          <div className="mx-3 mb-3 mt-3 rounded-md border border-slate-200 bg-slate-50 p-3">
            <div className="mb-2 text-sm text-slate-700">
              Loading financial data: {loadingProgress.loaded} of {loadingProgress.total} symbols
            </div>
            <Progress percent={loadingProgress.percent} status="active" strokeColor="#3F83F8" />
          </div>
        )}
        {activeTabData?.type === 'table' && !isLoading && hasFetchError && (
          <WidgetStatus
            action={
              <button type="button" className={widgetGhostButtonClassName} onClick={handleRetryFetch}>
                Try again
              </button>
            }
            className="m-3 min-h-64"
            description="Market data is temporarily unavailable. This is usually brief — try again in a moment."
            title="We couldn't load this data"
            tone="error"
          />
        )}
        {activeTabData?.type === 'table' && !hasFetchError && !isLoading && tableRows.length === 0 && (
          <WidgetStatus
            className="m-3 min-h-48"
            description={
              isEnriching
                ? 'Loading research data for your watchlist…'
                : 'This tab will populate once research data is available for your watchlist.'
            }
            title="No rows to display"
          />
        )}
        {activeTabData?.type === 'table' &&
          !hasFetchError &&
          !isLoading &&
          hasRankingsError &&
          activeTabData.key === 'rankings' &&
          tableRows.length > 0 && (
            <div className="mx-3 mb-3 flex flex-col gap-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-3 sm:flex-row sm:items-center sm:justify-between">
              <div>
                <p className="text-sm font-semibold text-slate-950">Edge Rankings temporarily unavailable</p>
                <p className="text-sm text-slate-600">
                  Scores couldn&apos;t be loaded for this watchlist. Symbols are still shown below.
                </p>
              </div>
              <button type="button" className={widgetGhostButtonClassName} onClick={handleRetryFetch}>
                Try again
              </button>
            </div>
          )}
        {activeTabData?.type === 'table' && !hasFetchError && (isLoading || tableRows.length > 0) && (
          <React.Suspense fallback={<div style={{ height: tableHeight }}>Loading...</div>}>
            {variant === 'watchlist' ? (
              <>
                {isLoading ? (
                  <WatchlistQuotesTableSkeleton />
                ) : (
                  <>
                    <div className={`sm:hidden ${WATCHLIST_QUOTES_TABLE_FRAME_CLASS}`}>
                      {tableRows.map(row => {
                        const changePercent = row.quote?.changePercent ?? 0;
                        const hasPrice = Boolean(row.quote?.lastTradePrice);
                        const secondaryLabel = getMobileRowLabel(row);
                        return (
                          <div
                            key={row.symbol}
                            className="flex items-center justify-between gap-3 border-b border-slate-100 px-4 py-3 last:border-0"
                          >
                            <div className="flex min-w-0 flex-1 items-center gap-2">
                              <TickerLogo size={20} src={logoBySymbol[row.symbol]} symbol={row.symbol} />
                              <div className={`min-w-0 ${WATCHLIST_QUOTES_SYMBOL_CELL_CLASS}`}>
                                <p className="truncate font-extrabold leading-4 text-bzblue-900">{row.symbol}</p>
                                <p className="mt-0.5 h-4 truncate text-xs leading-4 text-slate-500">
                                  {secondaryLabel ?? '—'}
                                </p>
                              </div>
                            </div>
                            <div className="shrink-0 text-right">
                              <p className="font-bold tabular-nums leading-tight text-slate-900">
                                {hasPrice ? currencyFormatter.format(row.quote?.lastTradePrice ?? 0) : '—'}
                              </p>
                              <p
                                className={`mt-0.5 text-xs font-semibold tabular-nums leading-tight ${
                                  !hasPrice
                                    ? 'text-slate-400'
                                    : changePercent >= 0
                                      ? 'text-bzgreen-500'
                                      : 'text-bzred-500'
                                }`}
                              >
                                {hasPrice ? `${changePercent >= 0 ? '+' : ''}${changePercent.toFixed(2)}%` : '—'}
                              </p>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                    <WatchlistQuotesTable rows={tableRows} />
                  </>
                )}
              </>
            ) : (
              <ResearchTableLoadingProvider isEnriching={isEnriching}>
                <div className="relative min-h-0 flex-1 overflow-hidden">
                  {isLoading && tableRows.length === 0 ? (
                    <ResearchTableSkeleton columns={researchSkeletonColumns} height={tableHeight} />
                  ) : (
                    <TableVirtualized
                      rowData={tableRows}
                      className="w-full overflow-x-auto [&_tbody_td]:h-[52px] [&_tbody_td]:align-middle"
                      columnsDef={activeTabData?.columns as ColumnDef<WatchlistFinancials>[]}
                      isLoading={false}
                      height={tableHeight}
                    />
                  )}
                  {activeTabData.key === 'rankings' && <TabPaywall />}
                </div>
              </ResearchTableLoadingProvider>
            )}
          </React.Suspense>
        )}
      </div>
      </TickerLogoLookupProvider>
    </ErrorBoundary>
  );
};

export default FinancialTable;
